import { EyeOutlined } from '@ant-design/icons';
import { Card, Col, Descriptions, Empty, Image, Row, Space, Typography } from 'antd';
import axios from 'axios';
import {
  Briefcase,
  Building2,
  FileDigit,
  Globe,
  Mail,
  Map,
  MapPin,
  MapPinned,
  Phone,
  PhoneCall,
  Store,
  Tag,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { CiLocationOn } from 'react-icons/ci';
import { IoDocumentTextOutline } from 'react-icons/io5';

import { getObjectFromArray } from '../../utils';
import FieldInput, { descriptionProps } from './FieldInput';
import './profilepage.less';

const { Text } = Typography;

/** Лого болон бичиг баримтын урьдчилсан харагдацын хэмжээ */
const PREVIEW_SIZE = 120;

/** Картын толгой: дүрс + текст */
const CardTitle = ({ icon: Icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <Icon style={{ display: 'block', fontSize: '1em' }} />
    {children}
  </span>
);

/** Жижиг сааралдуу тайлбар текст */
const Caption = ({ children }) => (
  <Text type="secondary" style={{ fontSize: 14 }}>
    {children}
  </Text>
);

/** Зураг: байхгүй үед хоосон дөрвөлжин */
const Preview = ({ src, alt }) =>
  src ? (
    <Image
      src={src}
      alt={alt}
      width={PREVIEW_SIZE}
      height={PREVIEW_SIZE}
      style={{
        objectFit: 'cover',
        borderRadius: 8,
        border: '1px solid #eaecf0',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)',
      }}
    />
  ) : (
    <div
      style={{
        width: PREVIEW_SIZE,
        height: PREVIEW_SIZE,
        borderRadius: 8,
        background: '#f2f4f7',
      }}
    />
  );

/**
 * Файлын өргөтгөл. Линк дээр query/hash (S3 подпись, кэшийн параметр) байвал
 * `slice(-3)` нь өргөтгөлийг танихгүй тул тэднийг эхлээд хаяна.
 */
const getExtension = (link) =>
  link?.split('?')[0]?.split('#')[0]?.split('.')?.pop()?.toLowerCase() ?? '';

/** Бичиг баримт: pdf бол дарахад шинэ цонхонд нээгдэнэ, эс бөгөөс зураг */
const Attachment = ({ link, label }) => (
  <Space direction="vertical" size={4}>
    <Caption>{label}</Caption>
    {getExtension(link) === 'pdf' ? (
      <div className="attachment__pdf" style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}>
        <embed className="attachment__pdf-embed" src={link} type="application/pdf" />
        {/*
          <embed> нь өөр дээрээ дарсан хулганы event-ийг залгидаг тул ороосон
          линк хүртэл дарагдал хүрдэггүй. Линкийг бүтэн дээгүүр нь тавьснаар
          зургийн аль ч хэсгийг дарахад шинэ цонх нээгдэнэ.
        */}
        <a
          className="attachment__pdf-link"
          href={link}
          target="_blank"
          rel="noreferrer nofollow noopener"
          aria-label={`${label} — шинэ цонхонд нээх`}
        >
          <span className="attachment__pdf-mask">
            <EyeOutlined /> Харах
          </span>
        </a>
      </div>
    ) : (
      <Preview src={link} alt={label} />
    )}
  </Space>
);

const InformationTab = () => {
  const [myInfo, setMyInfo] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('/api/v1/me/myInfo');
        setMyInfo(data?.result?.data ?? []);
      } catch (e) {
        console.error('Fetch My Info: ', e);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, []);

  /** Талбарын утгыг түлхүүрээр нь авна */
  const value = (key) => getObjectFromArray(myInfo, key)?.value;

  const affiliationType = useMemo(() => getObjectFromArray(myInfo, 'affiliation'), [myInfo]);
  const category = useMemo(() => getObjectFromArray(myInfo, 'category'), [myInfo]);

  /** O — байгууллага, эс бөгөөс хувь хүн */
  const isCompany = affiliationType?.value === 'O';

  const branchAddressValue = value('branchAddress');
  const branchAddress = Array.isArray(branchAddressValue)
    ? branchAddressValue.map((item) => item.name).join(', ')
    : branchAddressValue;

  const attachments = value('attachment') ?? [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        title={<CardTitle icon={AiOutlineInfoCircle}>Ерөнхий мэдээлэл</CardTitle>}
        size="middle"
        loading={loading}
      >
        <Row gutter={[16, 16]} align="top">
          <Col flex="none">
            <Space direction="vertical" size={4}>
              <Caption>Бизнесийн лого</Caption>
              <Preview src={value('brandLogo')} alt="Бизнесийн лого" />
            </Space>
          </Col>
          <Col flex="auto" style={{ minWidth: 240 }}>
            <Descriptions
              {...descriptionProps}
              items={[
                {
                  key: 'affiliation',
                  label: 'Компанийн төрөл',
                  children: (
                    <FieldInput
                      icon={Briefcase}
                      value={affiliationType?.enumDictionary?.[affiliationType?.value]}
                    />
                  ),
                },
                {
                  key: 'name',
                  label: isCompany ? 'Компанийн нэр' : 'Нэр',
                  children: <FieldInput icon={isCompany ? Building2 : User} value={value('name')} />,
                },
                {
                  key: 'registrationNumber',
                  label: 'Регистрийн дугаар',
                  children: <FieldInput icon={FileDigit} value={value('registrationNumber')} />,
                },
                {
                  key: 'category',
                  label: 'Үйл ажиллагааны чиглэл',
                  children: (
                    <FieldInput icon={Tag} value={category?.enumDictionary?.[category?.value]} />
                  ),
                },
                {
                  key: 'phoneNumber',
                  label: 'Утас',
                  children: <FieldInput icon={Phone} value={value('phoneNumber')} />,
                },
                {
                  key: 'email',
                  label: 'И-Мэйл',
                  children: <FieldInput icon={Mail} value={value('email')} />,
                },
                {
                  key: 'website',
                  label: 'Вэбсайт',
                  children: <FieldInput icon={Globe} value={value('website')} />,
                },
                {
                  key: 'address',
                  label: 'Бүртгэлтэй хаяг',
                  span: 2,
                  children: <FieldInput icon={MapPin} value={value('address')} wide />,
                },
              ]}
            />
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <CardTitle icon={IoDocumentTextOutline}>
            {isCompany ? 'Улсын бүртгэлийн гэрчилгээний хуулбар' : 'Иргэний үнэмлэх'}
          </CardTitle>
        }
        size="middle"
        loading={loading}
      >
        {attachments.length > 0 ? (
          <Space size={16} wrap align="start">
            {attachments.map((link, index) => (
              <Attachment key={link ?? index} link={link} label={index ? 'Ард тал' : 'Урд тал'} />
            ))}
          </Space>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Бичиг баримт байхгүй байна." />
        )}
      </Card>

      <Card
        title={<CardTitle icon={CiLocationOn}>Салбарын мэдээлэл</CardTitle>}
        size="middle"
        loading={loading}
      >
        <Descriptions
          {...descriptionProps}
          items={[
            {
              key: 'branchName',
              label: 'Салбарын нэр',
              children: <FieldInput icon={Store} value={value('branchName')} />,
            },
            {
              key: 'branchPhoneNumber',
              label: 'Салбарын утас',
              children: <FieldInput icon={PhoneCall} value={value('branchPhoneNumber')} />,
            },
            {
              key: 'zone',
              label: 'Салбарын бүс',
              children: <FieldInput icon={Map} value={value('zone')} />,
            },
            {
              key: 'branchAddress',
              label: 'Салбарын хаяг',
              span: 3,
              children: <FieldInput icon={MapPinned} value={branchAddress} wide />,
            },
          ]}
        />
      </Card>
    </Space>
  );
};

export default InformationTab;
